import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatMoney } from '../utils/currency';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Pagination from '../components/common/Pagination';
import Modal from '../components/common/Modal';
import { exportToCsv } from '../utils/exportCsv';
import { useAuth } from '../context/AuthContext';
import { canAny } from '../hooks/usePermissions';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { dateInputToBusinessUTC, todayBusinessIso } from '../utils/datetime';

const Payroll = () => {
  const { admin } = useAuth();
  const canManagePayouts = canAny(admin, ['manage_payouts', 'manage_payments']);

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [currencyCode, setCurrencyCode] = useState('QAR');

  // Filters
  const [search, setSearch] = useState('');
  const [positiveOnly, setPositiveOnly] = useState(true);
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');

  // Multi-select: driverId → amount (string for input control)
  const [selectedAmounts, setSelectedAmounts] = useState({});

  // Batch record modal
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [bankReference, setBankReference] = useState('');
  const [batchDate, setBatchDate] = useState('');
  const [batchNote, setBatchNote] = useState('');
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const searchDebounce = useRef(null);
  // Settlement max uses Qatar business timezone so the cutoff is consistent worldwide
  const todayIso = todayBusinessIso();

  const selectedIds = Object.keys(selectedAmounts);
  const selectedCount = selectedIds.length;
  const selectedTotal = selectedIds.reduce((sum, id) => sum + (Number(selectedAmounts[id]) || 0), 0);

  const fetchDrivers = useCallback(async (page = 1, searchVal = search, posOnly = positiveOnly) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 25 });
      if (searchVal.trim()) params.set('search', searchVal.trim());
      if (!posOnly) params.set('positiveOnly', 'false');
      const res = await api.get(`/admin/payroll?${params}`);
      setDrivers(res.data.data);
      setPagination(res.data.pagination);
      if (res.data.currencyCode) setCurrencyCode(String(res.data.currencyCode).toUpperCase());
    } catch {
      toast.error('Failed to load payroll list');
    } finally {
      setLoading(false);
    }
  }, [search, positiveOnly]);

  useEffect(() => {
    fetchDrivers(1, search, positiveOnly);
  }, [positiveOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => fetchDrivers(1, val, positiveOnly), 350);
  };

  const allCurrentSelected =
    drivers.length > 0 && drivers.every((d) => selectedAmounts[String(d._id)] !== undefined);

  const toggleSelectAll = () => {
    if (allCurrentSelected) {
      setSelectedAmounts((prev) => {
        const next = { ...prev };
        drivers.forEach((d) => delete next[String(d._id)]);
        return next;
      });
    } else {
      setSelectedAmounts((prev) => {
        const next = { ...prev };
        drivers.forEach((d) => {
          const key = String(d._id);
          if (next[key] === undefined) {
            next[key] = d.ledgerBalance > 0 ? String(d.ledgerBalance) : '0';
          }
        });
        return next;
      });
    }
  };

  const toggleRow = (driver) => {
    const key = String(driver._id);
    setSelectedAmounts((prev) => {
      if (prev[key] !== undefined) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: driver.ledgerBalance > 0 ? String(driver.ledgerBalance) : '0' };
    });
  };

  const updateAmount = (driverId, val) => {
    setSelectedAmounts((prev) => ({ ...prev, [String(driverId)]: val }));
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      const params = {};
      if (selectedIds.length > 0) params.driverIds = selectedIds.join(',');
      if (!positiveOnly) params.positiveOnly = 'false';
      // Settlement period boundaries anchored to Qatar business timezone
      if (periodFrom) params.periodFrom = dateInputToBusinessUTC(periodFrom, 'start');
      if (periodTo)   params.periodTo   = dateInputToBusinessUTC(periodTo,   'end');
      await exportToCsv('/admin/exports/payroll', params, `payroll-${stamp}.csv`);
    } catch {
      // exportToCsv handles its own error toast
    } finally {
      setExportLoading(false);
    }
  };

  const openBatchModal = () => {
    setBatchResults(null);
    setBankReference('');
    setBatchDate('');
    setBatchNote('');
    setBatchModalOpen(true);
  };

  const handleRecordPayouts = async () => {
    if (!bankReference.trim()) return;
    const items = selectedIds
      .map((id) => ({ driverId: id, amount: Number(selectedAmounts[id]) }))
      .filter((item) => item.amount > 0);

    if (items.length === 0) {
      toast.error('No valid amounts to record');
      return;
    }

    setBatchSubmitting(true);
    setBatchResults(null);
    try {
      const res = await api.post('/admin/payroll/record', {
        items,
        bankReference: bankReference.trim(),
        date: dateInputToBusinessUTC(batchDate, 'start') || undefined,
        note: batchNote.trim() || undefined,
      });

      const { recorded, failed, results } = res.data.data;
      setBatchResults({ recorded, failed, results });

      if (failed === 0) {
        toast.success(`${recorded} payout${recorded !== 1 ? 's' : ''} recorded successfully`);
        setBatchModalOpen(false);
        setSelectedAmounts({});
        fetchDrivers(1);
      } else {
        toast.warn(`Recorded ${recorded}, failed ${failed} — see details below`);
        fetchDrivers(1);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to record payouts');
    } finally {
      setBatchSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-1">
        <h1 className="text-2xl font-bold text-gray-800">Run Payroll</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Select drivers, export the sheet for your bank, transfer externally, then record all
        payouts at once.{' '}
        <Link to="/payouts" className="text-indigo-600 hover:underline">
          View payout history →
        </Link>
      </p>

      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={handleSearchChange}
          placeholder="Search by name or phone…"
          className="w-64 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={positiveOnly}
            onChange={(e) => {
              setSelectedAmounts({});
              setPositiveOnly(e.target.checked);
            }}
            className="w-4 h-4 text-indigo-600 rounded"
          />
          Positive balance only
        </label>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Period:</span>
          <input
            type="date"
            value={periodFrom}
            onChange={(e) => setPeriodFrom(e.target.value)}
            max={periodTo || todayIso}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <span className="text-gray-400">→</span>
          <input
            type="date"
            value={periodTo}
            onChange={(e) => setPeriodTo(e.target.value)}
            min={periodFrom}
            max={todayIso}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Action bar */}
      <div
        className={`flex items-center justify-between gap-4 mb-4 px-4 py-3 rounded-lg border transition-colors ${
          selectedCount > 0 ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200'
        }`}
      >
        <p className="text-sm font-medium text-gray-700">
          {selectedCount > 0 ? (
            <>
              <span className="text-indigo-700 font-semibold">{selectedCount}</span> driver
              {selectedCount !== 1 ? 's' : ''} selected · Total:{' '}
              <span className="font-semibold">{formatMoney(selectedTotal, currencyCode)}</span>
            </>
          ) : (
            'Select drivers below to export or record payouts'
          )}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={exportLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-700 border border-indigo-300 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-colors"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {exportLoading ? 'Exporting…' : 'Export Payroll Sheet'}
          </button>
          {canManagePayouts && (
            <button
              onClick={openBatchModal}
              disabled={selectedCount === 0}
              className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Record Payouts ({selectedCount})
            </button>
          )}
        </div>
      </div>

      {/* Driver table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {canManagePayouts && (
                    <th className="table-header w-10">
                      <input
                        type="checkbox"
                        checked={allCurrentSelected}
                        onChange={toggleSelectAll}
                        disabled={drivers.length === 0}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                    </th>
                  )}
                  <th className="table-header">Driver</th>
                  <th className="table-header">Phone</th>
                  <th className="table-header">Balance</th>
                  {canManagePayouts && <th className="table-header">Amount to Pay</th>}
                  <th className="table-header">Bank</th>
                  <th className="table-header">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {drivers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canManagePayouts ? 7 : 5}
                      className="table-cell text-center text-gray-500 py-8"
                    >
                      No drivers found
                    </td>
                  </tr>
                ) : (
                  drivers.map((driver) => {
                    const key = String(driver._id);
                    const isSelected = selectedAmounts[key] !== undefined;
                    return (
                      <tr
                        key={key}
                        className={`hover:bg-gray-50 ${isSelected ? 'bg-indigo-50/40' : ''}`}
                      >
                        {canManagePayouts && (
                          <td className="table-cell">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRow(driver)}
                              className="w-4 h-4 text-indigo-600 rounded"
                            />
                          </td>
                        )}
                        <td className="table-cell font-medium">
                          {driver.firstName} {driver.lastName}
                        </td>
                        <td className="table-cell text-sm text-gray-600">
                          {driver.phone || 'N/A'}
                        </td>
                        <td
                          className={`table-cell font-semibold ${
                            driver.ledgerBalance > 0 ? 'text-emerald-700' : 'text-gray-400'
                          }`}
                        >
                          {formatMoney(driver.ledgerBalance, currencyCode)}
                        </td>
                        {canManagePayouts && (
                          <td className="table-cell">
                            {isSelected ? (
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={selectedAmounts[key]}
                                onChange={(e) => updateAmount(key, e.target.value)}
                                className="w-28 px-2 py-1 text-sm border border-indigo-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              />
                            ) : (
                              <span className="text-gray-300 text-sm">—</span>
                            )}
                          </td>
                        )}
                        <td className="table-cell text-sm">
                          {driver.hasBankAccount ? (
                            <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
                              IBAN ✓
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">
                              No IBAN
                            </span>
                          )}
                        </td>
                        <td className="table-cell">
                          <StatusBadge status={driver.status} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            page={pagination.page}
            pages={pagination.pages}
            onPageChange={(p) => fetchDrivers(p)}
          />
        </div>
      )}

      {/* Batch Record Modal */}
      <Modal
        isOpen={batchModalOpen}
        onClose={() => { if (!batchSubmitting) setBatchModalOpen(false); }}
        title={`Record Payouts — ${selectedCount} Driver${selectedCount !== 1 ? 's' : ''}`}
        footer={
          !batchResults ? (
            <>
              <button
                type="button"
                onClick={() => setBatchModalOpen(false)}
                disabled={batchSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRecordPayouts}
                disabled={batchSubmitting || !bankReference.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {batchSubmitting
                  ? 'Recording…'
                  : `Record ${selectedCount} Payout${selectedCount !== 1 ? 's' : ''}`}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                setBatchModalOpen(false);
                if (batchResults.recorded > 0) setSelectedAmounts({});
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
            >
              Done
            </button>
          )
        }
      >
        {!batchResults ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2.5">
              <p className="text-xs text-gray-500">Total to record</p>
              <p className="text-lg font-semibold text-gray-800">
                {formatMoney(selectedTotal, currencyCode)}{' '}
                <span className="text-sm font-normal text-gray-500">
                  across {selectedCount} driver{selectedCount !== 1 ? 's' : ''}
                </span>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Batch Bank Reference <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={bankReference}
                onChange={(e) => setBankReference(e.target.value)}
                placeholder="e.g. PAYROLL-JUNE-2025"
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-400 mt-1">Applied to all payouts in this run</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transfer Date{' '}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="date"
                value={batchDate}
                onChange={(e) => setBatchDate(e.target.value)}
                max={todayIso}
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note{' '}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={batchNote}
                onChange={(e) => setBatchNote(e.target.value)}
                placeholder="e.g. June 2025 payroll run"
                className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="flex-1 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">{batchResults.recorded}</p>
                <p className="text-xs text-emerald-600 mt-0.5">Recorded</p>
              </div>
              {batchResults.failed > 0 && (
                <div className="flex-1 rounded-lg bg-red-50 border border-red-200 px-3 py-3 text-center">
                  <p className="text-2xl font-bold text-red-700">{batchResults.failed}</p>
                  <p className="text-xs text-red-600 mt-0.5">Failed</p>
                </div>
              )}
            </div>
            {batchResults.failed > 0 && (
              <div className="rounded-lg border border-red-200 overflow-hidden">
                <p className="text-xs font-medium text-red-700 bg-red-50 px-3 py-2">
                  Failed — re-check balance or IBAN and re-run
                </p>
                <ul className="divide-y divide-red-100 max-h-40 overflow-auto">
                  {batchResults.results
                    .filter((r) => !r.success)
                    .map((r) => (
                      <li key={r.driverId} className="px-3 py-2 text-xs text-red-600">
                        <span className="font-mono text-gray-500">…{r.driverId.slice(-6)}</span>:{' '}
                        {r.error}
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Payroll;
